import React, { useState } from 'react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { Edit, Trash2, X, Check } from 'lucide-react';
import { ALL_CATEGORIES, TRANSACTION_TYPES } from '../types';

const TransactionList = ({ 
  transactions, 
  loading, 
  error, 
  updateTransaction, 
  deleteTransaction 
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState(false);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY'
    }).format(amount);
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(transaction => {
    if (filter === 'all') return true;
    return transaction.type === filter;
  });

  // Start editing
  const startEdit = (transaction) => {
    setEditingId(transaction.id);
    setEditForm({
      type: transaction.type,
      category: transaction.category,
      amount: transaction.amount,
      memo: transaction.memo || '',
      date: transaction.date
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Save edit
  const saveEdit = async () => {
    if (!editForm.type || !editForm.category || !editForm.amount || !editForm.date) {
      alert('必須項目を入力してください');
      return;
    }

    setUpdating(true);
    const result = await updateTransaction(editingId, editForm);
    
    if (result.success) {
      setEditingId(null);
      setEditForm({});
    } else {
      alert(result.error || '更新に失敗しました');
    }
    setUpdating(false);
  };

  // Delete transaction
  const handleDelete = async (id) => {
    if (!window.confirm('この取引を削除しますか？')) {
      return;
    }

    const result = await deleteTransaction(id);
    
    if (!result.success) {
      alert(result.error || '削除に失敗しました');
    }
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        {error}
      </div>
    );
  }

  return (
    <div className="container">
      <h1>取引履歴</h1>
      
      {/* Filter */}
      <div className="card">
        <div className="form-group">
          <label>表示フィルター</label>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">すべて</option>
            <option value="income">収入のみ</option>
            <option value="expense">支出のみ</option>
          </select>
        </div>
      </div>

      {/* Transaction List */}
      <div className="card">
        {filteredTransactions.length === 0 ? (
          <div className="empty-state">
            <h3>取引がありません</h3>
            <p>新しい取引を追加してください</p>
          </div>
        ) : (
          <div className="transaction-list">
            {filteredTransactions.map((transaction) => (
              <div key={transaction.id} className="transaction-item">
                {editingId === transaction.id ? (
                  // Edit form
                  <div className="edit-form" style={{ width: '100%' }}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>タイプ</label>
                        <select
                          value={editForm.type}
                          onChange={(e) => handleFormChange('type', e.target.value)}
                        >
                          <option value="income">収入</option>
                          <option value="expense">支出</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>カテゴリー</label>
                        <select
                          value={editForm.category}
                          onChange={(e) => handleFormChange('category', e.target.value)}
                        >
                          {ALL_CATEGORIES[editForm.type]?.map(category => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>金額</label>
                        <input
                          type="number"
                          value={editForm.amount}
                          onChange={(e) => handleFormChange('amount', parseInt(e.target.value) || 0)}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>日付</label>
                        <input
                          type="date"
                          value={editForm.date}
                          onChange={(e) => handleFormChange('date', e.target.value)}
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>メモ</label>
                      <input
                        type="text"
                        value={editForm.memo}
                        onChange={(e) => handleFormChange('memo', e.target.value)}
                        placeholder="メモ（任意）"
                      />
                    </div>
                    
                    <div className="transaction-actions">
                      <button 
                        className="action-btn edit-btn"
                        onClick={saveEdit}
                        disabled={updating}
                      >
                        <Check size={16} />
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={cancelEdit}
                        disabled={updating}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display mode
                  <>
                    <div className="transaction-info">
                      <div className="transaction-category">
                        {transaction.category}
                      </div>
                      {transaction.memo && (
                        <div className="transaction-memo">
                          {transaction.memo}
                        </div>
                      )}
                      <div className="transaction-date">
                        {format(new Date(transaction.date), 'yyyy年M月d日', { locale: ja })}
                      </div>
                    </div>
                    <div className={`transaction-amount ${transaction.type}`}>
                      {transaction.type === 'income' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </div>
                    <div className="transaction-actions">
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => startEdit(transaction)}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(transaction.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionList;